import { Component, OnInit, AfterViewInit, Inject, DestroyRef, inject, HostListener, ElementRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { removeBlankStringCells } from '../../shared/utils/excel.utils';
import { SalaryService } from '../../core/services/salary.service';
import { KompBankService } from '../../core/services/komp-bank.service';
import { FortNoxService } from '../../core/services/fortnox.service';
import { OauthUtilService } from '../../core/services/oauth-util.service';
import { ModalService } from '../../core/services/modal.service';
import { DialogService } from '../../core/services/dialog.service';
import { FeatureFlagsService } from '../../core/services/feature-flags.service';
import {
  ExpenseModalComponent,
  ExpenseModalData as ExpenseModalViewData,
} from '../../shared/components/modal/expense-modal.component';
import { InternalTimeLockSummaryModalComponent } from '../../shared/components/modal/internal-time-lock-summary-modal.component';
import { TranslateStatusPipe } from '../../shared/pipes/translate-status.pipe';
import { DateHolidayService } from '../../core/services/date-holiday.service';
import { SwedishNumberPipe } from '../../shared/pipes/swedish-number.pipe';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import {
  SalaryFormData,
  CustMode,
  SalaryApiResponse,
  ExpenseGroupResponse,
  ExpenseApiItem,
  MileageApiItem,
  AllowanceApiItem,
  ExpenseDetail,
  ExpenseModalData,
  AbsenceDetail,
  FortNoxSalaryTransaction,
  FortNoxAbsenceData,
  FortNoxExpenseData,
  SalaryBulkItem,
  ProcessedSalaryRow,
  KarkaRow,
  SALARY_COLUMN,
  InternalTimeLockSummaryPerson,
  InternalTimeLockSummaryTotals,
} from './ng-salary.interfaces';

@Component({
  selector: 'ng-salary',
  template: require('./ng-salary.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./ng-salary.component.scss').default],
  standalone: false,
})
export class NgSalaryComponent implements OnInit, AfterViewInit {
  formData: SalaryFormData = {
    dateFrom: null,
    dateTo: null,
  };

  // State
  momentStart: moment.Moment;
  momentStop: moment.Moment;

  groupMode: 'T' | 'KU' | 'P' | 'A' | 'K' = 'T';
  custMode: CustMode = { external: true, internal: true, absense: true };
  subTotal = false;
  sumMode = 'A';
  timeMode = 'A';

  // Data
  invoice: SalaryApiResponse[] = [];
  expat: ProcessedSalaryRow[] = [];
  abscenseDetails: AbsenceDetail[] = [];
  expenseDetails: ExpenseDetail[] = [];
  hasFetched = false;
  requiresSalaryRefresh = true;
  internalTimeLockEnabled = false;
  overtimeQuickAddEnabled = false;

  showAbscenseDetails = false;
  showExpenseDetails = false;

  header: string[] = [];
  header2: string[] = [];
  header3: string[] = [];

  // Tracks checkboxes/state for mertid/overtid
  karka: KarkaRow[] = [];
  openActionMenuKey: string | null = null;
  private actionColumnSyncFrame: number | null = null;

  @ViewChild('salaryPageRoot') private salaryPageRoot?: ElementRef<HTMLElement>;
  @ViewChild('salaryPrimaryActionHeader') private salaryPrimaryActionHeader?: ElementRef<HTMLElement>;

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(SalaryService) private salaryService: SalaryService,
    @Inject(KompBankService) private kompBankService: KompBankService,
    @Inject(FortNoxService) private fortNoxService: FortNoxService,
    @Inject(OauthUtilService) private oauthUtil: OauthUtilService,
    @Inject(ModalService) private modalService: ModalService,
    @Inject(DialogService) private dialogService: DialogService,
    @Inject(DateHolidayService) private dateHolidayService: DateHolidayService,
    @Inject(FeatureFlagsService) private featureFlags: FeatureFlagsService
  ) {}

  ngOnInit() {
    this.oauthUtil.getAccessToken(); // Check/Init OAuth
    this.initDates();
    this.setupHeaders();
    this.featureFlags
      .getFlags$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((flags) => {
        this.internalTimeLockEnabled = !!flags.internalTimeLock;
        this.overtimeQuickAddEnabled = !!flags.overtimeQuickAdd;
        this.scheduleActionColumnSync();
      });
  }

  ngAfterViewInit(): void {
    this.scheduleActionColumnSync();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.report-row-action-menu')) {
      this.openActionMenuKey = null;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.openActionMenuKey = null;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleActionColumnSync();
  }

  toggleActionMenu(key: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openActionMenuKey = this.openActionMenuKey === key ? null : key;
  }

  closeActionMenu(): void {
    this.openActionMenuKey = null;
  }

  canTransferSalaryRow(row: ProcessedSalaryRow): boolean {
    const salaryTransactions = row[SALARY_COLUMN.SAL_TRANS];
    const hasSalaryTransactions = Array.isArray(salaryTransactions) && salaryTransactions.length > 0 && salaryTransactions[0] != null;
    const totalCompensation =
      Number(row[SALARY_COLUMN.TOTAL_BONUS] || 0) +
      Number(row[SALARY_COLUMN.MERTID_COMP] || 0) +
      Number(row[SALARY_COLUMN.OVERTID_COMP] || 0);
    return (
      !hasSalaryTransactions &&
      totalCompensation > 0
    );
  }

  canRemoveSalaryRow(row: ProcessedSalaryRow): boolean {
    const salaryTransactions = row[SALARY_COLUMN.SAL_TRANS];
    return Array.isArray(salaryTransactions) && salaryTransactions.length > 0 && salaryTransactions[0] != null;
  }

  canAddMertid(row: ProcessedSalaryRow): boolean {
    return Number(row[SALARY_COLUMN.MERTID] || 0) > 0;
  }

  canAddOvertid(row: ProcessedSalaryRow): boolean {
    return Number(row[SALARY_COLUMN.OVERTID] || 0) > 0;
  }

  hasSalaryRowActions(row: ProcessedSalaryRow): boolean {
    return (
      this.canTransferSalaryRow(row) ||
      this.canRemoveSalaryRow(row)
    );
  }

  hasAbsenceActions(row: AbsenceDetail): boolean {
    return true;
  }

  canAddExpenseRow(row: ExpenseDetail): boolean {
    return !row.salary_id && !this.isRejected(row);
  }

  canRejectExpenseRow(row: ExpenseDetail): boolean {
    return !row.salary_id && this.isApproved(row);
  }

  initDates() {
    if (moment().date() > 25) {
      this.momentStart = moment().startOf('day').startOf('month');
    } else {
      this.momentStart = moment().startOf('day').startOf('month').subtract(1, 'month');
    }
    this.momentStop = moment(this.momentStart).add(1, 'month').subtract(1, 'days');

    this.formData.dateFrom = this.momentStart.toDate();
    this.formData.dateTo = this.momentStop.toDate();
  }

  updatePeriod(dir: number) {
    this.momentStart = moment(this.formData.dateFrom);
    this.momentStop = moment(this.formData.dateTo);

    if (dir === -1) {
      this.momentStart.startOf('month').subtract(1, 'months');
      this.momentStop.startOf('month').subtract(1, 'days');
    } else {
      this.momentStart.startOf('month').add(1, 'months');
      this.momentStop.startOf('month').add(2, 'months').subtract(1, 'days');
    }
    this.formData.dateFrom = this.momentStart.toDate();
    this.formData.dateTo = this.momentStop.toDate();
    this.requiresSalaryRefresh = true;
  }

  onStartDateChange(date: moment.Moment): void {
    this.momentStart = date;
    this.formData.dateFrom = date.toDate();
    this.requiresSalaryRefresh = true;
  }

  onStopDateChange(date: moment.Moment): void {
    this.momentStop = date;
    this.formData.dateTo = date.toDate();
    this.requiresSalaryRefresh = true;
  }

  private buildInternalLockTotals(rows: InternalTimeLockSummaryPerson[]): InternalTimeLockSummaryTotals {
    return rows.reduce(
      (totals, row) => ({
        total_rows: totals.total_rows + Number(row.total_rows || 0),
        total_hours: totals.total_hours + Number(row.total_hours || 0),
        locked_rows: totals.locked_rows + Number(row.locked_rows || 0),
        locked_hours: totals.locked_hours + Number(row.locked_hours || 0),
        lockable_rows: totals.lockable_rows + Number(row.lockable_rows || 0),
        lockable_hours: totals.lockable_hours + Number(row.lockable_hours || 0),
      }),
      {
        total_rows: 0,
        total_hours: 0,
        locked_rows: 0,
        locked_hours: 0,
        lockable_rows: 0,
        lockable_hours: 0,
      }
    );
  }

  onDateChange() {
    // Sync moments when datepicker changes
    this.momentStart = moment(this.formData.dateFrom);
    this.momentStop = moment(this.formData.dateTo);
  }

  canPreviewAndLockInternalTime(): boolean {
    return this.hasFetched && !this.requiresSalaryRefresh;
  }

  async previewAndLockInternalTime(): Promise<void> {
    this.onDateChange();

    const from = this.momentStart.format('YYYY-MM-DD');
    const to = this.momentStop.format('YYYY-MM-DD');

    this.salaryService
      .previewInternalTimeLock(from, to)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async (rows) => {
          if (!rows.length) {
            await this.dialogService.alert({
              title: 'Ingen interntid hittades',
              body: 'Det finns ingen intern tid att låsa för den valda perioden.',
              buttonText: 'OK',
            });
            return;
          }

          const totals = this.buildInternalLockTotals(rows);
          const confirmed = await this.modalService.show(InternalTimeLockSummaryModalComponent, {
            from,
            to,
            rows,
            totals,
          });

          if (!confirmed) {
            return;
          }

          this.salaryService
            .lockInternalTime(from, to)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: async () => {
                await this.dialogService.alert({
                  title: 'Interntid låst',
                  body: `Interntid för perioden ${from} - ${to} har låsts.`,
                  buttonText: 'OK',
                });
                this.fetchSalary();
              },
              error: (err) => this.handleError(err),
            });
        },
        error: (err) => this.handleError(err),
      });
  }

  fetchSalary() {
    this.onDateChange(); // Ensure sync
    this.hasFetched = true;
    this.showAbscenseDetails = false;
    this.exportData = []; // Legacy for CSV, maybe needed
    this.invoice = [];
    this.abscenseDetails = [];
    this.karka = [];

    this.setupHeaders();

    const workHours = this.calculateWorkHours(this.momentStart, this.momentStop);

    // FETCH SALARY
    this.salaryService
      .get<SalaryApiResponse>({
        subTotal: this.subTotal,
        sum: this.sumMode,
        custMode: this.custMode,
        per: this.timeMode,
        type: this.groupMode,
        id: 0,
        from: this.momentStart.format('YYYY-MM-DD'),
        to: this.momentStop.format('YYYY-MM-DD'),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.expat = [];
        data.forEach((item, index) => {
          item.periodlength = workHours;

          // Setup expat array rows based on header
          this.expat[index] = {} as ProcessedSalaryRow;
          this.processSalaryRow(this.header, item, this.expat[index]);

          // Handle saltrans (existing FortNox transactions)
          this.expat[index][SALARY_COLUMN.SAL_TRANS] = [];
          if (item.saltrans) {
            item.saltrans.forEach((st: string) => {
              (this.expat[index][SALARY_COLUMN.SAL_TRANS] as FortNoxSalaryTransaction[]).push(JSON.parse(st));
            });
          }
        });
        this.invoice = data;
        this.requiresSalaryRefresh = false;
        this.scheduleActionColumnSync();
      });

    // FETCH ABSENCE
    if (this.groupMode === 'T') {
      this.salaryService
        .get<AbsenceDetail>({
          subTotal: this.subTotal,
          sum: this.sumMode,
          custMode: { external: false, internal: false, absense: true },
          per: this.timeMode,
          type: 'K',
          id: 0,
          from: this.momentStart.format('YYYY-MM-DD'),
          to: this.momentStop.format('YYYY-MM-DD'),
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((data) => {
          this.abscenseDetails = [];
          data.forEach((item) => {
            this.abscenseDetails.push(item);
            if (item.salary_id === null) {
              this.showAbscenseDetails = true;
            }
          });
          this.scheduleActionColumnSync();
        });
    } else {
      this.abscenseDetails = [];
      this.scheduleActionColumnSync();
    }

    // FETCH EXPENSES
    this.salaryService
      .get<ExpenseGroupResponse>({
        custMode: { external: false, internal: false, absense: true },
        per: this.timeMode,
        type: 'E',
        id: 0,
        from: this.momentStart.format('YYYY-MM-DD'),
        to: this.momentStop.format('YYYY-MM-DD'),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.expenseDetails = [];
        data.forEach((group) => {
          if (group.expenses)
            group.expenses.forEach((d) =>
              this.expenseDetails.push(this.mapExpense(d, 'Utlägg'))
            );
          if (group.mileages)
            group.mileages.forEach((d) =>
              this.expenseDetails.push(this.mapExpense(d, 'Milersättning'))
            );
          if (group.allowances)
            group.allowances.forEach((d) =>
              this.expenseDetails.push(this.mapExpense(d, 'Traktamente'))
            );
        });
        if (this.expenseDetails.length > 0) this.showExpenseDetails = true;
        this.scheduleActionColumnSync();
      });
  }

  private scheduleActionColumnSync(): void {
    if (this.actionColumnSyncFrame !== null) {
      cancelAnimationFrame(this.actionColumnSyncFrame);
    }

    this.actionColumnSyncFrame = requestAnimationFrame(() => {
      this.actionColumnSyncFrame = null;
      this.syncActionColumnWidth();
    });
  }

  private syncActionColumnWidth(): void {
    const root = this.salaryPageRoot?.nativeElement;
    if (!root) {
      return;
    }

    const header = this.salaryPrimaryActionHeader?.nativeElement;
    if (!header) {
      root.style.removeProperty('--salary-action-column-width');
      return;
    }

    const width = Math.ceil(header.getBoundingClientRect().width);
    if (width > 0) {
      root.style.setProperty('--salary-action-column-width', `${width}px`);
    }
  }

  // Helpers
  calculateWorkHours(start: moment.Moment, stop: moment.Moment): number {
    return this.dateHolidayService.getWorkHoursBetween(start, stop);
  }

  setupHeaders() {
    switch (this.groupMode) {
      case 'KU':
        this.header = ['ID', 'Namn', 'Kund', 'Summa'];
        break;
      case 'A':
        this.header = ['ID', 'Namn', 'Kund', 'Projekt', 'Aktivitet', 'Summa'];
        break;
      case 'P':
        this.header = ['ID', 'Namn', 'Kund', 'Projekt', 'Summa'];
        break;
      case 'K':
        this.header = ['ID', 'Namn', 'Datum', 'Kund', 'Projekt', 'Aktivitet', 'Timmar'];
        break;
      case 'T':
        this.header = [
          'ID',
          'Namn',
          'Lön',
          'Bonus',
          'Normaltid',
          'Arbetat',
          'Komp uttag',
          'Sjuk',
          'Diff',
          'Bonus grund',
          'Ej Bonus',
          'Semester',
          'Mertid',
          'Övertid',
          'Beläggning',
          'Sjukavdrag',
          'Karensavdrag',
          'Sem Bonus',
          'Lönebonus',
          'Tot bonus',
          'Mertid ers',
          'Övertid ers',
          'Kompsaldo',
        ];
        this.header2 = ['ID', 'Namn', 'Datum', 'Kund', 'Projekt', 'Aktivitet', 'Timmar'];
        this.header3 = ['ID', 'Namn', 'Datum', 'Typ', 'Spec', 'Summa', 'Status'];
        break;
    }
  }

  isNumericColumn(headerName: string): boolean {
    const numericHeaders = [
      'Summa',
      'Timmar',
      'Beläggning',
      'Lön',
      'Bonus',
      'Normaltid',
      'Arbetat',
      'Diff',
      'Mertid',
      'Övertid',
      'Komp uttag',
      'Sjuk',
      'Sjukavdrag',
      'Karensavdrag',
      'Bonus grund',
      'Ej Bonus',
      'Semester',
      'Beläggning',
      'Sem Bonus',
      'Lönebonus',
      'Tot bonus',
      'Mertid ers',
      'Övertid ers',
      'Kompsaldo',
    ];
    return numericHeaders.includes(headerName);
  }

  private getExpenseStatus(detail: ExpenseDetail): string {
    const payload = detail.payload as unknown as Record<string, unknown>;
    return (
      (payload.expense_status as string | undefined) ||
      (payload.mileage_status as string | undefined) ||
      (payload.allowance_status as string | undefined) ||
      ''
    );
  }

  private applySheetFormats(worksheet: XLSX.WorkSheet, headers: string[], rowCount: number): void {
    for (let row = 1; row <= rowCount; row++) {
      headers.forEach((header, col) => {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = worksheet[cellAddress];
        if (!cell || cell.v === '' || cell.v === null || cell.v === undefined) {
          return;
        }

        if (header === 'Datum') {
          const dateParts = String(cell.v).split('-');
          if (dateParts.length === 3) {
            cell.t = 'd';
            cell.v = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]));
            cell.z = 'yyyy-mm-dd';
          }
          return;
        }

        if (this.isNumericColumn(header)) {
          const numValue = typeof cell.v === 'number' ? cell.v : Number(cell.v);
          if (!Number.isNaN(numValue)) {
            cell.t = 'n';
            cell.v = numValue;
            cell.z = Number.isInteger(numValue) ? '0' : '0.00';
          }
        }
      });
    }
  }

  exportToExcel(): void {
    if (!this.invoice.length) {
      return;
    }

    const workbook = XLSX.utils.book_new();

    const salaryRows = [
      this.header,
      ...this.expat.map((row) => this.header.map((_, index) => row[index] ?? '')),
    ];
    const salarySheet = XLSX.utils.aoa_to_sheet(salaryRows);
    removeBlankStringCells(salarySheet);
    this.applySheetFormats(salarySheet, this.header, this.expat.length);
    XLSX.utils.book_append_sheet(workbook, salarySheet, 'Lön');

    if (this.abscenseDetails.length > 0) {
      const absenceRows = [
        this.header2,
        ...this.abscenseDetails.map((detail) =>
          this.header2.map((head) => this.getValueForHeader(head, detail) ?? '')
        ),
      ];
      const absenceSheet = XLSX.utils.aoa_to_sheet(absenceRows);
      removeBlankStringCells(absenceSheet);
      this.applySheetFormats(absenceSheet, this.header2, this.abscenseDetails.length);
      XLSX.utils.book_append_sheet(workbook, absenceSheet, 'Frånvaro');
    }

    if (this.expenseDetails.length > 0) {
      const expenseRows = [
        this.header3,
        ...this.expenseDetails.map((detail) => [
          ...detail.row,
          this.getExpenseStatus(detail),
        ]),
      ];
      const expenseSheet = XLSX.utils.aoa_to_sheet(expenseRows);
      removeBlankStringCells(expenseSheet);
      this.applySheetFormats(expenseSheet, this.header3, this.expenseDetails.length);
      XLSX.utils.book_append_sheet(workbook, expenseSheet, 'Utlägg');
    }

    XLSX.writeFile(workbook, 'lonerapport.xlsx');
  }

  processSalaryRow(header: string[], dataIn: SalaryApiResponse, dataOut: ProcessedSalaryRow): void {
    let timlon, sjuklon, karenstimmar, sjuktimmar, karensavdrag, sjukloneavdrag;

    header.forEach((curr, index) => {
      switch (curr) {
        case 'Kompsaldo':
          dataOut[index] = dataIn.kompbank;
          break;
        case 'Summa':
          dataOut[index] = dataIn.sum;
          break;
        case 'Kund':
          dataOut[index] = dataIn.customername;
          break;
        case 'Projekt':
          dataOut[index] = dataIn.project_name;
          break;
        case 'Aktivitet':
          dataOut[index] = dataIn.activity_name;
          break;
        case 'Timmar':
          dataOut[index] = dataIn.time;
          break;
        case 'Datum':
          dataOut[index] = dataIn.reporteddate;
          break;
        case 'Namn':
          dataOut[index] = dataIn.name;
          break;
        case 'Arbetat':
          dataOut[index] = dataIn.tot - (dataIn.komp_ut || 0);
          break;
        case 'ID':
          dataOut[index] = dataIn.employee_number;
          break;
        case 'Normaltid':
          dataOut[index] = dataIn.periodlength;
          break;
        case 'Komp uttag':
          dataOut[index] = dataIn.komp_ut;
          break;
        case 'Diff':
          dataIn.diff = dataIn.tot - dataIn.periodlength;
          dataOut[index] = dataIn.diff;
          break;
        case 'Bonus':
          dataOut[index] = dataIn.bonus;
          break;
        case 'Bonus grund':
          dataIn.bonusgr =
            (dataIn.debit || 0) +
            (dataIn.int_komp || 0) +
            (dataIn.int_proj || 0) +
            (dataIn.internt || 0) +
            (dataIn.oh || 0) +
            (dataIn.semester || 0) +
            (dataIn.komp_ut || 0) +
            (dataIn.sjuk || 0);
          dataOut[index] = dataIn.bonusgr ? dataIn.bonusgr : 0;
          break;
        case 'Mertid':
          // Math.min(inv.tjl, inv.diff) ? Math.min(inv.tjl, inv.diff) : 0
          dataIn.mertid = Math.min(dataIn.tjl || 0, dataIn.diff || 0); // Using truthy check as in legacy?
          // Legacy: Math.min(tjl, diff) ? ... : 0. If min is < 0 or 0 it returns 0?
          // Wait, if tjl is 0, min is 0. 0 ? 0 : 0. Correct.
          // If diff is negative, min is negative. negative ? negative : 0.
          // JS: -5 is truthy. So it returns -5? Legacy logic seems to allow negative mertid?
          // Let's stick to strict replication:
          const val = Math.min(dataIn.tjl || 0, dataIn.diff || 0);
          dataIn.mertid = val ? val : 0;
          dataOut[index] = dataIn.mertid;
          break;
        case 'Mertid ers':
          dataOut[index] = (dataIn.mertid * 1 * (dataIn.salary + dataIn.bonus)) / 175;
          break;
        case 'Övertid ers':
          dataOut[index] = (dataIn.overtid * 1.5 * (dataIn.salary + dataIn.bonus)) / 175;
          break;
        case 'Övertid':
          dataIn.overtid = dataIn.diff - dataIn.mertid;
          dataOut[index] = dataIn.overtid;
          break;
        case 'Ej Bonus':
          dataIn.ejbonus = (dataIn.idle || 0) + (dataIn.tjl || 0);
          dataOut[index] = dataIn.ejbonus ? dataIn.ejbonus : 0;
          break;
        case 'Beläggning':
          const periodLen = dataIn.periodlength || 1; // Avoid div by zero
          dataIn.bel = Math.min(dataIn.bonusgr / periodLen, 1);
          dataOut[index] = dataIn.bel;
          break;
        case 'Semester':
          dataOut[index] = dataIn.semester / 8;
          break;
        case 'Sem Bonus':
          const semBonus = dataIn.bonus * 0.14 * (dataIn.semester / 175);
          dataOut[index] = semBonus;
          break;
        case 'Lön':
          dataOut[index] = dataIn.salary;
          break;
        case 'Lönebonus':
          dataIn.totBonus = dataIn.bel * dataIn.bonus;
          if (dataIn.sjuk > 0) {
            karenstimmar = dataIn.sjuk - Math.max(dataIn.sjuk - 8, 0);
            sjuktimmar = Math.max(dataIn.sjuk - 8, 0);

            timlon = (dataIn.bonus * 12) / (52 * 40);
            sjuklon = 0.8 * timlon;

            karensavdrag = sjuklon * karenstimmar;
            sjukloneavdrag = (timlon - sjuklon) * dataIn.sjuk; // Logic from legacy seems weird: (tim - sjuk) * sjuk
            // Legacy: sjuklöneavdrag = (timlön - sjuklön) * dataIn.sjuk;

            dataIn.totBonus = dataIn.totBonus - sjukloneavdrag - karensavdrag;
          }
          dataOut[index] = dataIn.bel * dataIn.bonus; // Wait, legacy sets dataOut to bel*bonus but updates totBonus?
          // Legacy line 561: dataOut[index] = dataIn.bel * dataIn.bonus;
          // But lines 538-560 modify dataIn.totBonus.
          // And 'Lönebonus' display IS dataIn.bel * dataIn.bonus.
          // 'Tot bonus' column (case below) uses dataIn.totBonus.
          break;
        case 'Sjuk':
          dataOut[index] = dataIn.sjuk;
          break;
        case 'Sjukavdrag':
          if (dataIn.sjuk > 0) {
            timlon = (dataIn.bonus * 12) / (52 * 40);
            sjuklon = 0.8 * timlon;
            dataOut[index] = -(timlon - sjuklon) * dataIn.sjuk;
          } else {
            dataOut[index] = 0;
          }
          break;
        case 'Karensavdrag':
          if (dataIn.sjuk > 0) {
            timlon = (dataIn.bonus * 12) / (52 * 40);
            sjuklon = 0.8 * timlon;
            karenstimmar = dataIn.sjuk - Math.max(dataIn.sjuk - 8, 0);
            dataOut[index] = -sjuklon * karenstimmar;
          } else {
            dataOut[index] = 0;
          }
          break;
        case 'Tot bonus':
          dataOut[index] = dataIn.totBonus + dataIn.bonus * 0.14 * (dataIn.semester / 175);
          break;
        default:
          dataOut[index] = '';
          break;
      }
    });
  }

  getValueForHeader(header: string, data: SalaryApiResponse | AbsenceDetail): string | number {
    switch (header) {
      case 'ID':
        return data.employee_number;
      case 'Namn':
        return data.name;
      case 'Datum':
        return data.reporteddate ? moment(data.reporteddate).format('YYYY-MM-DD') : '';
      case 'Kund':
        return data.customername;
      case 'Projekt':
        return data.project_name;
      case 'Aktivitet':
        return data.activity_name;
      case 'Timmar':
        return data.time;
      default:
        return '';
    }
  }

  mapExpense(
    data: ExpenseApiItem | MileageApiItem | AllowanceApiItem,
    type: string
  ): ExpenseDetail {
    let spec = '';
    let outlay_type: 'expense' | 'mileage' | 'allowance';
    if (type === 'Utlägg' && 'expense_date' in data.payload) {
      outlay_type = 'expense';
      spec = `${moment(data.payload.expense_date).format('YYYY-MM-DD')}: ${data.payload.specification}`;
    } else if (type === 'Milersättning' && 'mileage_date' in data.payload) {
      outlay_type = 'mileage';
      spec = `${moment(data.payload.mileage_date).format('YYYY-MM-DD')}: ${data.payload.km_reimbursement}km ${data.payload.specification}`;
    } else {
      outlay_type = 'allowance';
      if ('start_date_time' in data.payload) {
        spec = `${moment(data.payload.start_date_time).format('YYYY-MM-DD')}-${moment(data.payload.end_date_time).format('YYYY-MM-DD')}: ${data.payload.specification}`;
      }
    }

    return {
      row: [
        data.employee_number,
        data.name,
        moment(data.report_date).format('YYYY-MM-DD'),
        type,
        spec,
        data.payload.total_amount,
      ],
      salary_id: data.salary_id,
      payload: data.payload,
      internal_id: data.id,
      outlay_type,
    };
  }

  // Operations
  showExpense(expense: ExpenseDetail): void {
    // Hämta full payload (inkl. bilder) on-demand – listan saknar binärdata
    this.salaryService.getExpensePayload(expense.outlay_type, expense.internal_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fullPayload) => {
          this.modalService.show(ExpenseModalComponent, {
            expense: this.createExpenseObj({ ...expense, payload: fullPayload }),
            headerText: 'Granska',
          });
        },
        error: (err) => this.handleError(err),
      });
  }

  createExpenseObj(expense: ExpenseDetail): ExpenseModalViewData {
    const payload = expense.payload;
    let date = '';
    let startDateTime: string | undefined;
    let endDateTime: string | undefined;
    let vat: number | undefined;
    let exclVat: number | undefined;
    let participants: string | undefined;
    let distanceKm: number | undefined;
    let kmReimbursement: number | undefined;
    let receipts: string[] = [];

    if ('expense_date' in payload) {
      date = payload.expense_date;
      // Expense Manager sends the amount excluding VAT as vat_amount; 0 means unknown.
      if (payload.vat_amount > 0) {
        exclVat = Number(payload.vat_amount);
        vat = Math.round(((payload.total_amount - exclVat) + Number.EPSILON) * 100) / 100;
      }
      receipts = (payload.images || payload.receipts || []).map((r) => {
        if (typeof r === 'string') return r;
        return r.data || '';
      });
    } else if ('mileage_date' in payload) {
      date = payload.mileage_date;
      distanceKm = payload.distance;
      kmReimbursement = payload.km_reimbursement;
    } else if ('start_date_time' in payload) {
      date = payload.allowance_date || payload.start_date_time;
      startDateTime = payload.start_date_time;
      endDateTime = payload.end_date_time;
      participants = payload.participants;
    }

    return {
      specification: payload.specification,
      date,
      start_date_time: startDateTime,
      end_date_time: endDateTime,
      type: expense.row[3],
      total: payload.total_amount,
      vat,
      exclVat,
      participants,
      distance_km: distanceKm,
      km_reimbursement: kmReimbursement,
      receipts,
    };
  }

  // --- Operations ---

  // Salary Operations
  async salAdd(row: ProcessedSalaryRow, index: number): Promise<void> {
    const dateStr = this.momentStop.format('YYYY-MM-DD');

    const rorlig: FortNoxSalaryTransaction = {
      EmployeeId: row[SALARY_COLUMN.ID] as string,
      SalaryCode: 135,
      Total: row[SALARY_COLUMN.TOTAL_BONUS] as number,
      Date: dateStr,
      TextRow: 'Rörlig',
    };

    if (!row[SALARY_COLUMN.SAL_TRANS]) row[SALARY_COLUMN.SAL_TRANS] = [];
    const salTrans = row[SALARY_COLUMN.SAL_TRANS] as FortNoxSalaryTransaction[];

    try {
      if (rorlig.Total > 0) {
        const resp = await this.fortNoxService
          .addSalaryItem({
            salary: rorlig,
            redirect_uri: this.oauthUtil.fullUrl(),
          })
          .toPromise();
        row[SALARY_COLUMN.SAL_TRANS] = [...salTrans, resp.SalaryTransaction];
      }

      const mert: FortNoxSalaryTransaction = { ...rorlig, Total: row[SALARY_COLUMN.MERTID_COMP] as number, TextRow: 'Mertid' };
      if (mert.Total > 0) {
        const resp = await this.fortNoxService
          .addSalaryItem({
            salary: mert,
            redirect_uri: this.oauthUtil.fullUrl(),
          })
          .toPromise();
        row[SALARY_COLUMN.SAL_TRANS] = [...(row[SALARY_COLUMN.SAL_TRANS] as FortNoxSalaryTransaction[]), resp.SalaryTransaction];
      }

      const overt: FortNoxSalaryTransaction = { ...rorlig, Total: row[SALARY_COLUMN.OVERTID_COMP] as number, TextRow: 'Övertid' };
      if (overt.Total > 0) {
        const resp = await this.fortNoxService
          .addSalaryItem({
            salary: overt,
            redirect_uri: this.oauthUtil.fullUrl(),
          })
          .toPromise();
        row[SALARY_COLUMN.SAL_TRANS] = [...(row[SALARY_COLUMN.SAL_TRANS] as FortNoxSalaryTransaction[]), resp.SalaryTransaction];
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  salDel(row: ProcessedSalaryRow, index: number): void {
    const salTrans = row[SALARY_COLUMN.SAL_TRANS] as FortNoxSalaryTransaction[] | null;
    if (!salTrans || salTrans.length === 0) return;

    const salDetails = [...salTrans];

    this.fortNoxService
      .deleteSalaryItems({
        salary: salDetails,
        redirect_uri: this.oauthUtil.fullUrl(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp: Array<{ error: string | null }>) => {
          let message = '';
          resp.forEach((r, i: number) => {
            if (r.error !== null) {
              message += JSON.stringify(salDetails[i]) + JSON.stringify(r);
            }
          });
          if (message.length > 0) {
            this.dialogService.alert({
              title: 'Fel vid borttagning',
              body: message,
              buttonText: 'OK',
              variant: 'danger',
            });
          }
          row[SALARY_COLUMN.SAL_TRANS] = [];
        },
        error: (err) => this.handleError(err),
      });
  }

  salAddAll(): void {
    const sal: SalaryBulkItem[] = [];
    const dateStr = this.momentStop.format('YYYY-MM-DD');

    // Build array aligned with expat - push empty objects for already transferred rows
    this.expat.forEach((row) => {
      const salTrans = row[SALARY_COLUMN.SAL_TRANS] as FortNoxSalaryTransaction[] | null;
      const saldet: SalaryBulkItem = {
        rorlig: { EmployeeId: '', SalaryCode: 0, Total: 0, Date: '', TextRow: '' },
        mert: { EmployeeId: '', SalaryCode: 0, Total: 0, Date: '', TextRow: '' },
        overt: { EmployeeId: '', SalaryCode: 0, Total: 0, Date: '', TextRow: '' },
      };

      if (!salTrans || salTrans.length === 0 || salTrans[0] == null) {
        saldet.rorlig = {
          EmployeeId: row[SALARY_COLUMN.ID] as string,
          SalaryCode: 135,
          Total: row[SALARY_COLUMN.TOTAL_BONUS] as number,
          Date: dateStr,
          TextRow: 'Rörlig',
        };
        saldet.mert = { ...saldet.rorlig, Total: row[SALARY_COLUMN.MERTID_COMP] as number, TextRow: 'Mertid' };
        saldet.overt = { ...saldet.rorlig, Total: row[SALARY_COLUMN.OVERTID_COMP] as number, TextRow: 'Övertid' };
      }
      sal.push(saldet);
    });

    const hasPending = sal.some((s) => s.rorlig.EmployeeId !== '');
    if (!hasPending) return;

    this.fortNoxService
      .bulkSalaryItems({
        salary: sal,
        redirect_uri: this.oauthUtil.fullUrl(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp: Array<Array<{ fortnox: { SalaryTransaction: FortNoxSalaryTransaction } | null; db: any; error: any }>>) => {
          // resp[j] = array of {fortnox, db, error} for rorlig/mert/overt – aligns 1:1 with expat
          resp.forEach((employeeResults, j: number) => {
            if (!this.expat[j]) return;
            const newTrans = employeeResults
              .filter((r) => r.fortnox?.SalaryTransaction)
              .map((r) => r.fortnox!.SalaryTransaction);
            if (newTrans.length > 0) {
              const existing = (this.expat[j][SALARY_COLUMN.SAL_TRANS] as FortNoxSalaryTransaction[]) || [];
              this.expat[j][SALARY_COLUMN.SAL_TRANS] = [...existing, ...newTrans];
            }
          });
        },
        error: (err) => this.handleError(err),
      });
  }

  // Absence Operations
  absCreate(absDetail: AbsenceDetail): void {
    const data: FortNoxAbsenceData = {
      EmployeeId: absDetail.employee_number,
      CauseCode: absDetail.salary_code,
      Date: moment(absDetail.reporteddate).format('YYYY-MM-DD'),
      Hours: absDetail.time,
      RecordId: absDetail.record_id,
    };

    this.fortNoxService
      .postAbsence({
        absence: data,
        redirect_uri: this.oauthUtil.fullUrl(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => (absDetail.salary_id = Math.ceil(absDetail.time)), // Mark as done
        error: (err) => this.handleError(err),
      });
  }

  absDel(absDetail: AbsenceDetail): void {
    const data: FortNoxAbsenceData = {
      EmployeeId: absDetail.employee_number,
      CauseCode: absDetail.salary_code,
      Date: moment(absDetail.reporteddate).format('YYYY-MM-DD'),
      Hours: absDetail.time,
      RecordId: absDetail.record_id,
    };
    this.fortNoxService
      .deleteAbsence({
        absence: data,
        redirect_uri: this.oauthUtil.fullUrl(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => (absDetail.salary_id = null),
        error: (err) => this.handleError(err),
      });
  }

  absCreateAll(absDetails: AbsenceDetail[]): void {
    const abs: FortNoxAbsenceData[] = [];
    const pending = absDetails.filter((a) => a.salary_id === null);

    pending.forEach((a) => {
      abs.push({
        EmployeeId: a.employee_number,
        CauseCode: a.salary_code,
        Date: moment(a.reporteddate).format('YYYY-MM-DD'),
        Hours: a.time,
        RecordId: a.record_id,
      });
    });

    if (abs.length === 0) return;

    this.fortNoxService
      .bulkAbsence({
        absence: abs,
        redirect_uri: this.oauthUtil.fullUrl(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp: Array<{ fortnox: any; db: any; error: any }>) => {
          // resp[k] corresponds to pending[k] in order – mark successful items as done
          resp.forEach((r, k: number) => {
            if (r.error === null && pending[k]) {
              pending[k].salary_id = Math.ceil(pending[k].time);
            }
          });
        },
        error: (err) => this.handleError(err),
      });
  }

  // Expense Operations

  /** Kärnan – kastar vid fel så att anroparen styr felhantering */
  private async addExpenseCore(inv3: ExpenseDetail): Promise<void> {
    const expense: FortNoxExpenseData = {
      EmployeeId: inv3.row[0],
      SalaryCode: '892',
      Expense: '90',
      Total: inv3.row[5],
      Date: inv3.row[2],
      TextRow: inv3.row[4],
    };

    const resp = await this.fortNoxService
      .addExpenseItem({
        salary: expense,
        redirect_uri: this.oauthUtil.fullUrl(),
        outlay_type: inv3.outlay_type,
        internal_id: inv3.internal_id,
        name: inv3.row[1],
      })
      .toPromise();

    inv3.salary_id = resp.expenseResp.SalaryTransaction.SalaryRow;
    if ('expense_status' in inv3.payload) inv3.payload.expense_status = 'PAID';
    else if ('mileage_status' in inv3.payload) inv3.payload.mileage_status = 'PAID';
    else if ('allowance_status' in inv3.payload) inv3.payload.allowance_status = 'PAID';
  }

  async addExpense(inv3: ExpenseDetail): Promise<void> {
    try {
      await this.addExpenseCore(inv3);
    } catch (err) {
      this.handleError(err);
    }
  }

  async expenseAddAll(expenseDetails: ExpenseDetail[]): Promise<void> {
    const pending = expenseDetails.filter((inv3) => !inv3.salary_id && !this.isRejected(inv3));
    if (pending.length === 0) return;

    const items = pending.map((inv3) => ({
      salary: {
        EmployeeId: inv3.row[0],
        SalaryCode: '892',
        Expense: '90',
        Total: inv3.row[5],
        Date: inv3.row[2],
        TextRow: inv3.row[4],
      } as FortNoxExpenseData,
      redirect_uri: this.oauthUtil.fullUrl(),
      outlay_type: inv3.outlay_type,
      internal_id: inv3.internal_id,
      name: inv3.row[1],
    }));

    let responses: any[];
    try {
      responses = await this.fortNoxService.bulkExpenseItems({ items }).toPromise();
    } catch (err) {
      this.handleError(err);
      return;
    }

    for (let i = 0; i < responses.length; i++) {
      const { result, error } = responses[i];
      if (error) {
        this.handleError({ error: { ErrorInformation: { message: error } } });
        break;
      }
      const inv3 = pending[i];
      inv3.salary_id = result.expenseResp.SalaryTransaction.SalaryRow;
      if ('expense_status' in inv3.payload) inv3.payload.expense_status = 'PAID';
      else if ('mileage_status' in inv3.payload) inv3.payload.mileage_status = 'PAID';
      else if ('allowance_status' in inv3.payload) inv3.payload.allowance_status = 'PAID';
    }
  }

  async rejectExpense(inv3: ExpenseDetail): Promise<void> {
    const confirmed = await this.dialogService.confirm({
      title: 'Avvisa utlägg',
      body: `Vill du avvisa utlägget för ${inv3.row[1]} (${inv3.row[2]}, ${inv3.row[5]} kr)?`,
      confirmText: 'Avvisa',
      cancelText: 'Avbryt',
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    const expenseInfo = {
      expense_id: inv3.outlay_type === 'expense' ? inv3.internal_id : undefined,
      mileage_id: inv3.outlay_type === 'mileage' ? inv3.internal_id : undefined,
      allowance_id: inv3.outlay_type === 'allowance' ? inv3.internal_id : undefined,
      reason: 'Avvisad i kitstajm',
    };
    try {
      await this.fortNoxService.deleteExpenseItem(expenseInfo).toPromise();
      if ('expense_status' in inv3.payload) inv3.payload.expense_status = 'REJECTED';
      else if ('mileage_status' in inv3.payload) inv3.payload.mileage_status = 'REJECTED';
      else if ('allowance_status' in inv3.payload) inv3.payload.allowance_status = 'REJECTED';
    } catch (err) {
      this.handleError(err);
    }
  }

  // Helpers & Komp
  addKomp(row: ProcessedSalaryRow, index: number): void {
    const mertid = (row[SALARY_COLUMN.MERTID] as number) || 0;
    if (mertid > 0) {
      let dd = 1;
      if (mertid < 1) dd = mertid;

      row[SALARY_COLUMN.MERTID] = mertid - dd;
      row[SALARY_COLUMN.COMP_BALANCE] = ((row[SALARY_COLUMN.COMP_BALANCE] as number) || 0) + dd;

      if (!this.karka[index]) this.karka[index] = [];
      this.karka[index][SALARY_COLUMN.MERTID] = true;
      this.karka[index][SALARY_COLUMN.MERTID_COMP] = true;
      this.karka[index][SALARY_COLUMN.COMP_BALANCE] = true;
    }
  }

  addKomp2(row: ProcessedSalaryRow, index: number): void {
    const overtid = (row[SALARY_COLUMN.OVERTID] as number) || 0;
    if (overtid > 0) {
      let dd = 1;
      if (overtid < 1) dd = overtid;

      row[SALARY_COLUMN.OVERTID] = overtid - dd;
      row[SALARY_COLUMN.COMP_BALANCE] = ((row[SALARY_COLUMN.COMP_BALANCE] as number) || 0) + dd;

      if (!this.karka[index]) this.karka[index] = [];
      this.karka[index][SALARY_COLUMN.OVERTID] = true;
      this.karka[index][SALARY_COLUMN.OVERTID_COMP] = true;
      this.karka[index][SALARY_COLUMN.COMP_BALANCE] = true;
    }
  }

  handleError(error: { status?: number; error?: { ErrorInformation?: any }; resource?: string }): void {
    console.error(error);
    if (error.status === 433) {
      this.oauthUtil.oauthLoginHandler(error);
    } else {
      const ei = error.error?.ErrorInformation;
      const msg = (ei && typeof ei === 'object' ? ei.Message : ei) || JSON.stringify(error);
      this.dialogService.alert({
        title: 'Fel',
        body: msg,
        buttonText: 'OK',
        variant: 'danger',
      });
    }
  }

  isRejected(inv3: ExpenseDetail): boolean {
    const payload = inv3.payload;
    const status =
      ('expense_status' in payload && payload.expense_status) ||
      ('mileage_status' in payload && payload.mileage_status) ||
      ('allowance_status' in payload && payload.allowance_status);
    return status === 'REJECTED';
  }

  isApproved(inv3: ExpenseDetail): boolean {
    const payload = inv3.payload;
    const status =
      ('expense_status' in payload && payload.expense_status) ||
      ('mileage_status' in payload && payload.mileage_status) ||
      ('allowance_status' in payload && payload.allowance_status);
    return status === 'APPROVED';
  }

  // Placeholder for exportData needed for CSV if migrating CSV export
  exportData: SalaryApiResponse[] = [];
}
